/* eslint id-length: 0 */
import {
  EY_CERTIFICATES_FIELD_NAMES_EDUCATIONAL,
  EY_CERTIFICATES_FIELD_NAMES_OTHER
} from "../helpers/custom_pptx_template";
import { CustomType } from "../parts/ey_pptx_preview/types";
import {
  customImagePosition,
  defaultImagePosition,
  eyBlack,
  eyWhite,
  eyYellow,
  fullHeight,
  fullWidth,
  imageSize,
  leftOffset,
  ptsToInch,
  sidebarWidth,
  spaceSize,
  topOffset
} from "./constants";
import { getAdjustedHeight, PptxgenJsTextOptions } from "./helpers";

const filterCustomTypes = (customTypes: CustomType[], names: string[]) =>
  customTypes
    .filter(({ type, text }) => text && names.includes(type.name))
    .map(({ text }) => text.split("\r"))
    .flat();

export const sidebarHeaderOptions = {
  breakLine: true,
  color: eyYellow,
  bold: true,
  underline: { color: eyYellow, style: "sng" },
  fontSize: 10
};

export const textOptions = {
  breakLine: true,
  color: eyWhite,
  italic: true,
  fontFace: "EYInterstate",
  fontSize: 9,
  lineSpacing: 10
};

const rightPanelHeaderOptions = {
  x: sidebarWidth + spaceSize,
  w: fullWidth - sidebarWidth - 2 * spaceSize,
  h: ptsToInch(25),
  valign: "middle",
  align: "left",
  fontFace: "EYInterstate",
  fontSize: 10,
  lineSpacing: 11,
  color: eyBlack,
  fill: eyYellow,
  bold: true,
  line: { width: "2", color: eyBlack },
  breakLine: true,
  italic: false
};

const rightPanelTextOptions = {
  ...textOptions,
  color: eyBlack,
  line: { width: "2", color: eyBlack },
  x: sidebarWidth + spaceSize,
  w: fullWidth - sidebarWidth - 2 * spaceSize,
  fontSize: 8
};

const summaryHeaderOptions = { y: topOffset, ...rightPanelHeaderOptions };

const getSummaryTextOptions = (summary?: string) => ({
  ...rightPanelTextOptions,
  y: summaryHeaderOptions.y + summaryHeaderOptions.h + spaceSize,
  h: summary ? getAdjustedHeight(summary, { ...rightPanelTextOptions, h: ptsToInch(100) }) : ptsToInch(100)
});

const getExperienceHeaderOptions = (hasSummary: boolean, summaryTextOptions: PptxgenJsTextOptions) => ({
  ...rightPanelHeaderOptions,
  y: hasSummary
    ? (summaryTextOptions.y || 0) + (summaryTextOptions.h || 0) + spaceSize
    : summaryHeaderOptions.y
});

const getExperienceTextOptions = (
  experienceHeaderOptions: PptxgenJsTextOptions,
  certificatesHeaderOptions: PptxgenJsTextOptions,
  showCertificates: boolean
) => {
  const certificatesSpace = showCertificates
    ? fullHeight - (certificatesHeaderOptions.y || 0) + spaceSize
    : 0;
  const y = (experienceHeaderOptions.y || 0) + (experienceHeaderOptions.h || 0) + spaceSize;
  return {
    ...rightPanelTextOptions,
    y,
    h: fullHeight - y - certificatesSpace
  };
};

const getCertificatesHeaderOptions = (certificatesTextOptions: PptxgenJsTextOptions) => ({
  ...rightPanelHeaderOptions,
  y: (certificatesTextOptions.y || 0) - rightPanelHeaderOptions.h - spaceSize,
  w: certificatesTextOptions.w,
  x: certificatesTextOptions.x
});

const getCertificatesTextOptions = (
  filteredCertificatesTypes: CustomType[],
  logoOptions: PptxgenJsTextOptions,
  showEducationalCertificates: boolean,
  showOtherCertificates: boolean
) => {
  const split = showEducationalCertificates && showOtherCertificates;
  const w = split ? (rightPanelTextOptions.w - spaceSize) / 2 : rightPanelTextOptions.w;
  const textOptions = {
    ...rightPanelTextOptions,
    h: ptsToInch(100),
    w
  };

  const getHeight = (textRows: string[]) =>
    Math.max(
      getAdjustedHeight(textRows.join("\n"), textOptions),
      fullHeight - (logoOptions.y || 0) + spaceSize
    );

  const textRowsEducational = filterCustomTypes(
    filteredCertificatesTypes,
    EY_CERTIFICATES_FIELD_NAMES_EDUCATIONAL
  );
  const textRowsOther = filterCustomTypes(filteredCertificatesTypes, EY_CERTIFICATES_FIELD_NAMES_OTHER);

  const h = Math.max(getHeight(textRowsEducational), getHeight(textRowsOther));

  const educationalCertificatesTextOptions = {
    ...textOptions,
    y: fullHeight - h,
    h,
    lineSpacing: 9
  };
  const otherCertificatesTextOptions = {
    ...educationalCertificatesTextOptions,
    ...(split ? { x: rightPanelTextOptions.x + (rightPanelTextOptions.w - spaceSize) / 2 + spaceSize } : {})
  };

  return {
    educationalCertificatesTextOptions,
    otherCertificatesTextOptions
  };
};

const getLeftSidePanelTextOptions = (hasAvatar: boolean) => ({
  ...textOptions,
  x: leftOffset,
  w: sidebarWidth,
  h: fullHeight - (hasAvatar ? 2 * spaceSize + imageSize : 0),
  y: topOffset + (hasAvatar ? 2 * spaceSize + imageSize : 2 * spaceSize)
});

type UseEYPptxOptionsProps = {
  avatar: object;
  summary?: string;
  showCertificates: boolean;
  showEducationalCertificates: boolean;
  showOtherCertificates: boolean;
  filteredCertificatesTypes: CustomType[];
  logo: { custom: boolean; url: string };
};

export const useEYPptxOptions = ({
  summary,
  showCertificates,
  showEducationalCertificates,
  showOtherCertificates,
  filteredCertificatesTypes,
  avatar,
  logo
}: UseEYPptxOptionsProps) => {
  const logoOptions = logo.custom
    ? {
        ...customImagePosition,
        data: logo.url
      }
    : {
        ...defaultImagePosition,
        path: logo.url
      };

  const summaryTextOptions = getSummaryTextOptions(summary);
  const experienceHeaderOptions = getExperienceHeaderOptions(!!summary, summaryTextOptions);
  const { educationalCertificatesTextOptions, otherCertificatesTextOptions } = getCertificatesTextOptions(
    filteredCertificatesTypes,
    logoOptions,
    showEducationalCertificates,
    showOtherCertificates
  );
  const educationalCertificatesHeaderOptions = getCertificatesHeaderOptions(
    educationalCertificatesTextOptions
  );
  const otherCertificatesHeaderOptions = getCertificatesHeaderOptions(otherCertificatesTextOptions);
  const experienceTextOptions = getExperienceTextOptions(
    experienceHeaderOptions,
    educationalCertificatesHeaderOptions,
    showCertificates
  );
  const leftSidePanelTextOptions = getLeftSidePanelTextOptions(!!avatar);

  const slideConfig = {
    title: "RESUME",
    background: { color: "FFFFFF" },
    objects: [
      {
        rect: {
          x: leftOffset,
          y: topOffset,
          w: sidebarWidth,
          h: fullHeight,
          fill: { color: eyBlack }
        }
      },
      {
        placeholder: {
          options: {
            name: "leftSidePanelText",
            type: "body",
            ...leftSidePanelTextOptions
          },
          text: ""
        }
      },
      ...(summary
        ? [
            {
              placeholder: {
                options: {
                  name: "summaryHeader",
                  type: "title",
                  ...summaryHeaderOptions
                },
                text: ""
              }
            },
            {
              placeholder: {
                options: {
                  name: "summaryText",
                  type: "body",
                  ...summaryTextOptions
                },
                text: ""
              }
            }
          ]
        : []),
      {
        placeholder: {
          options: {
            name: "experienceHeader",
            type: "title",
            ...experienceHeaderOptions
          },
          text: ""
        }
      },
      {
        placeholder: {
          options: {
            name: "experienceText",
            type: "body",
            ...experienceTextOptions
          },
          text: ""
        }
      },
      ...(showEducationalCertificates
        ? [
            {
              placeholder: {
                options: {
                  name: "educationalCertificatesHeader",
                  type: "title",
                  ...educationalCertificatesHeaderOptions
                },
                text: ""
              }
            },
            {
              placeholder: {
                options: {
                  name: "educationalCertificatesText",
                  type: "body",
                  ...educationalCertificatesTextOptions
                },
                text: ""
              }
            }
          ]
        : []),
      ...(showOtherCertificates
        ? [
            {
              placeholder: {
                options: {
                  name: "otherCertificatesHeader",
                  type: "title",
                  ...otherCertificatesHeaderOptions
                },
                text: ""
              }
            },
            {
              placeholder: {
                options: {
                  name: "otherCertificatesText",
                  type: "body",
                  ...otherCertificatesTextOptions
                },
                text: ""
              }
            }
          ]
        : [])
    ]
  };

  return {
    educationalCertificatesHeaderOptions,
    otherCertificatesHeaderOptions,
    educationalCertificatesTextOptions,
    otherCertificatesTextOptions,
    experienceHeaderOptions,
    leftSidePanelTextOptions,
    sidebarHeaderOptions,
    summaryHeaderOptions,
    experienceTextOptions,
    summaryTextOptions,
    rightPanelHeaderOptions,
    rightPanelTextOptions,
    textOptions,
    slideConfig,
    logoOptions
  };
};
